import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useRemoveCartItemMutation } from "../api/cartApi";
import { useAddWishlistItemMutation } from "../../wishlist/api/wishlistApi";
import { useGetCartQuery } from "../api/cartApi";

/**
 * Moves an item from Cart to Wishlist via frontend orchestration.
 * 1. Calls AddToWishlist (using Product/Parent ID).
 * 2. If success, Calls RemoveFromCart (using Item Key).
 */
export const useMoveCartItemToWishlist = () => {
  const { t } = useTranslation();
  const [addWishlistItem] = useAddWishlistItemMutation();
  const [removeCartItem] = useRemoveCartItemMutation();
  const { data: cart } = useGetCartQuery();

  const [isMoving, setIsMoving] = useState(false);

  const handleMoveCartItemToWishlist = useCallback(
    async (key: string) => {
      // Find the item in the cart cache to get IDs
      const item = cart?.items.find((i) => i.key === key);
      if (!item) return;

      setIsMoving(true);
      const toastId = toast.loading(
        t("cartItemActions.moving", "Moving to wishlist...")
      );

      try {
        // Step 1: Add to Wishlist
        // We pass both IDs; the backend decides which to store (usually Parent ID)
        await addWishlistItem({
          productId: item.id,
          parentId: item.parentId,
        }).unwrap();

        // Step 2: Remove from Cart
        await removeCartItem({ key }).unwrap();

        toast.success(
          t("feedback.wishlist.addedSuccess", "Moved to wishlist!"),
          { id: toastId }
        );
      } catch (error) {
        console.error("Move to wishlist failed", error);
        toast.error(
          t("feedback.wishlist.toggleError", "Failed to move item."),
          { id: toastId }
        );
      } finally {
        setIsMoving(false);
      }
    },
    [addWishlistItem, removeCartItem, cart, t]
  );

  return { handleMoveCartItemToWishlist, isMoving };
};
